import React from "react";
import { Select } from "antd5";
import { BaseSelectRef } from "rc-select/lib";

import UserInitials, { UnassignedIcon } from "components/comments/UserInitials";
import UserName, { formatUserNameWithStatus } from "components/users/UserName";
import { useCompany } from "lib/hooks/api/company/useCompany";
import { CheckboxOption } from "./formUtils";

type Option = {
  label: string;
  value: string;
  renderOption: React.ReactNode;
};

type Props = {
  onChange: (values: { ids?: string[]; includeNulls?: boolean }) => void;
  value?: { ids?: string[]; includeNulls?: boolean };
};

const UNASSIGNED = "unassigned";

const AssigneeFilter = React.forwardRef<BaseSelectRef, Props>(({ onChange, value }, ref) => {
  const { data: company, isLoading: isLoadingCompany } = useCompany({ includeInactive: true });

  const options: Option[] =
    (company?.users ?? []).map((user) => ({
      value: user.guid,
      label: formatUserNameWithStatus(user),
      renderOption: (
        <CheckboxOption
          id={user.guid}
          checked={value?.ids?.includes(user.guid) ?? false}
          text={<UserName user={user} />}
          icon={
            <UserInitials
              firstName={user?.firstName}
              lastName={user?.lastName}
              showTooltip={false}
              type="extra-small"
            />
          }
        />
      ),
    })) || [];

  const unassignedOption = {
    label: "Unassigned",
    value: UNASSIGNED,
    renderOption: (
      <CheckboxOption
        id={UNASSIGNED}
        checked={value?.includeNulls ?? false}
        text="Unassigned"
        icon={<UnassignedIcon showTooltip={false} type="extra-small" />}
      />
    ),
  };

  const allOptions = [unassignedOption, ...options];

  const handleSelect = (values: string[]) => {
    const selectedAssignees = values.filter((assignee) => assignee !== UNASSIGNED);
    const includeUnassigned = !!values.find((assignee) => assignee === UNASSIGNED);

    onChange({
      ids: selectedAssignees,
      includeNulls: includeUnassigned,
    });
  };

  return (
    <Select
      ref={ref}
      mode="multiple"
      placeholder="Select assignee"
      options={allOptions}
      menuItemSelectedIcon={null}
      value={value?.includeNulls ? [...(value?.ids ?? []), UNASSIGNED] : value?.ids}
      loading={isLoadingCompany}
      optionRender={({ data }) => data.renderOption}
      onChange={handleSelect}
    />
  );
});

AssigneeFilter.displayName = "AssigneeFilter";

export default AssigneeFilter;
